import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import * as Sentry from '@sentry/react'
import axios from 'axios'
import i18next from 'i18next'
import { GW, matchesGatewayPath } from '@/shared/api/endpoints'
import { LANGUAGES_DASHBOARD_TYPES } from '@/shared/constants/languages'
import { useDashboardDisplayLanguageStore } from '@/shared/store/display-language-store'
import { getAdminScopeFromPath, getLoginPathForScope } from './admin-routes'
import { toCamelCaseKeys, toSnakeCaseKeys } from './case-transform'
import {
  getStoredLanguageCode,
  getStoredTenantId,
  getStoredToken,
  getStoredUser,
  syncAuthLogin,
  syncAuthLogout,
} from './get-auth-token'
import { apiLogger } from './logger'
import { showToast } from './toast'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Omit the auto-injected X-Language-Code header, e.g. to fetch all localizedData languages at once. */
    skipLanguageHeader?: boolean
  }
}

interface ApiEnvelope<T> {
  data: T | null
}

interface RefreshTokenResponse {
  access_token: string
  refresh_token?: string
}

type RequestData = Record<string, unknown> | FormData | URLSearchParams | string | null | undefined

const DEFAULT_ACCEPT_LANGUAGE = 'vi-VN,vi;q=0.9,en-US;q=0.8,en;q=0.7'

/**
 * Auth URLs the 401 handler must recognise, derived from the same `GW` builder the
 * services use so they cannot drift from `VITE_PATH_IAM`.
 */
const AUTH_LOGIN_URL = GW.iam('auth/login')
const AUTH_REFRESH_URL = GW.iam('auth/refresh')

const ACCEPT_LANGUAGE_BY_DASHBOARD_LANGUAGE: Record<string, string> = {
  [LANGUAGES_DASHBOARD_TYPES.ENG]: 'en-AU,en-GB;q=0.9,en-US;q=0.8,en;q=0.7,vi;q=0.6',
  [LANGUAGES_DASHBOARD_TYPES.VI]: DEFAULT_ACCEPT_LANGUAGE,
}

function asHeaderValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined

  const normalized = value.trim()
  return normalized.length > 0 ? normalized : undefined
}

function getDashboardAcceptLanguage(): string {
  const activeLanguage = useDashboardDisplayLanguageStore.getState().activeLanguage
  return ACCEPT_LANGUAGE_BY_DASHBOARD_LANGUAGE[activeLanguage] ?? DEFAULT_ACCEPT_LANGUAGE
}

function getRequestHeader(
  config: InternalAxiosRequestConfig,
  headerName: string,
): string | undefined {
  if (!config.headers) {
    return undefined
  }

  return (
    asHeaderValue(config.headers[headerName]) ??
    asHeaderValue(config.headers[headerName.toLowerCase()])
  )
}

/** Guards against duplicate toast + redirect when many requests fail 401 at once. */
let sessionExpiryRedirectScheduled = false

/** Shared in-flight refresh promise so concurrent 401s reuse a single /auth/refresh call. */
let refreshAccessTokenPromise: Promise<string> | null = null

const SESSION_EXPIRED_REDIRECT_MS = 1200

function clearAuthStorageAndStore(): void {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  sessionStorage.removeItem('refresh_token')
  syncAuthLogout()
}

/**
 * Final auth failure path (access + refresh both unusable):
 * clears persisted session, shows the session-expired toast, then redirects to login.
 * Idempotent — only the first call schedules the redirect.
 *
 * @param captureError - Optional error forwarded to Sentry to explain why session ended.
 */
function scheduleSessionExpiryRedirect(captureError?: unknown): void {
  if (sessionExpiryRedirectScheduled) return
  sessionExpiryRedirectScheduled = true
  clearAuthStorageAndStore()
  if (captureError !== undefined) {
    Sentry.captureException(captureError, {
      tags: {
        type: 'auth_refresh_failed',
      },
    })
  }
  showToast.error(i18next.t('auth.sessionExpired'))
  window.setTimeout(() => {
    window.location.href = getLoginPathForScope(getAdminScopeFromPath(window.location.pathname))
  }, SESSION_EXPIRED_REDIRECT_MS)
}

/**
 * Returns a shared promise that resolves with a fresh access token.
 *
 * Concurrent 401s from many in-flight requests must not each call `/auth/refresh`
 * independently: when the backend rotates refresh tokens, only the first call
 * succeeds and the rest fail with an invalidated RT, forcing the user out.
 * This helper guarantees at most one refresh request per expiry burst — all
 * other callers await the same promise. The promise is cleared on settle so
 * the next expiry burst can refresh again.
 *
 * Side effects on success: persists the new access token (and rotated refresh
 * token, if returned) to the same storage the original RT lived in, and
 * updates the in-memory auth store via `syncAuthLogin`.
 *
 * Side effects on failure: triggers `scheduleSessionExpiryRedirect` and
 * rejects so callers can surface the error to their own catch path.
 *
 * @returns Promise resolving to the new access token string.
 * @throws The refresh request error if the network call fails or the response is missing `access_token`.
 */
async function getOrCreateRefreshPromise(): Promise<string> {
  if (refreshAccessTokenPromise) {
    return refreshAccessTokenPromise
  }

  refreshAccessTokenPromise = (async () => {
    const hasLocalRefreshToken = localStorage.getItem('refresh_token') !== null
    const refreshToken =
      localStorage.getItem('refresh_token') ?? sessionStorage.getItem('refresh_token')

    if (!refreshToken) {
      scheduleSessionExpiryRedirect()
      throw new Error('Missing refresh token')
    }

    try {
      const response = await axios.post<ApiEnvelope<RefreshTokenResponse>>(
        AUTH_REFRESH_URL,
        { refresh_token: refreshToken },
        { baseURL: import.meta.env.VITE_API_URL },
      )
      const refreshData = response.data.data
      if (!refreshData?.access_token) {
        throw new Error('Refresh token response is missing access_token')
      }

      const currentUser = getStoredUser()
      if (currentUser) {
        syncAuthLogin(currentUser, refreshData.access_token)
      }

      if (refreshData.refresh_token) {
        const refreshStorage = hasLocalRefreshToken ? localStorage : sessionStorage
        const otherStorage = hasLocalRefreshToken ? sessionStorage : localStorage
        refreshStorage.setItem('refresh_token', refreshData.refresh_token)
        otherStorage.removeItem('refresh_token')
      }

      return refreshData.access_token
    } catch (refreshError) {
      scheduleSessionExpiryRedirect(refreshError)
      throw refreshError
    }
  })().finally(() => {
    refreshAccessTokenPromise = null
  })

  return refreshAccessTokenPromise
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const tenantId = asHeaderValue(getStoredTenantId())
    const languageCode = asHeaderValue(getStoredLanguageCode())
    const requestTenantId = config.headers
      ? asHeaderValue(config.headers['X-Tenant-Id'])
      : undefined
    if (tenantId && config.headers && !requestTenantId) {
      config.headers['X-Tenant-Id'] = tenantId
    }
    const requestLanguageCode = config.headers
      ? asHeaderValue(config.headers['X-Language-Code'])
      : undefined
    if (languageCode && config.headers && !requestLanguageCode && !config.skipLanguageHeader) {
      config.headers['X-Language-Code'] = languageCode
    }
    const requestAcceptLanguage = getRequestHeader(config, 'Accept-Language')
    if (config.headers && !requestAcceptLanguage) {
      config.headers['Accept-Language'] = getDashboardAcceptLanguage()
    }

    const transformedData = toSnakeCaseKeys(config.data as unknown)
    config.data = transformedData

    // Log request
    const requestData = config.data as RequestData
    apiLogger.info('Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      data: requestData,
    })

    // Add Sentry breadcrumb for request
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: 'info',
      data: {
        url: config.url,
        method: config.method,
      },
    })

    return config
  },
  async (error: AxiosError) => {
    apiLogger.error('Request Error:', error)

    // Capture request errors in Sentry
    Sentry.captureException(error, {
      tags: {
        type: 'request_error',
      },
    })

    return Promise.reject(error)
  },
)

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const transformedResponseData = toCamelCaseKeys(response.data as unknown)
    response.data = transformedResponseData

    // Log response
    const responseData = response.data as unknown
    apiLogger.info('Response:', {
      status: response.status,
      url: response.config.url,
      data: responseData,
    })

    // Add Sentry breadcrumb for successful response
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${response.status} ${response.config.url}`,
      level: 'info',
      data: {
        status: response.status,
        url: response.config.url,
      },
    })

    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (error.response) {
      error.response.data = toCamelCaseKeys(error.response.data)
    }

    // Log error
    apiLogger.error('Response Error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data,
    })

    // Capture API errors in Sentry (except 401 during retry)
    const shouldCapture = !(error.response?.status === 401 && !originalRequest._retry)
    if (shouldCapture) {
      Sentry.captureException(error, {
        tags: {
          http_status: error.response?.status,
          http_method: error.config?.method,
          type: 'api_error',
        },
        contexts: {
          response: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            data: error.response?.data,
          },
        },
      })
    }

    // Handle 401 Unauthorized — refresh access token; if refresh fails the session is treated as expired.
    const requestUrl = originalRequest.url ?? ''
    const isRefreshRequest = matchesGatewayPath(requestUrl, AUTH_REFRESH_URL)
    const isLoginRequest = matchesGatewayPath(requestUrl, AUTH_LOGIN_URL)

    if (error.response?.status === 401 && isLoginRequest) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && isRefreshRequest) {
      scheduleSessionExpiryRedirect(error)
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true

      try {
        const newAccessToken = await getOrCreateRefreshPromise()
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }
        return await axiosInstance(originalRequest)
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      apiLogger.error('Access forbidden')
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      apiLogger.error('Resource not found')
    }

    // Handle 500 Server Error
    if (error.response?.status === 500) {
      apiLogger.error('Internal server error')
    }

    return Promise.reject(error)
  },
)

export const request = {
  get: async <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return axiosInstance.get<T>(url, config)
  },

  post: async <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance.post<T>(url, data, config)
  },

  put: async <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance.put<T>(url, data, config)
  },

  patch: async <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance.patch<T>(url, data, config)
  },

  delete: async <T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance.delete<T>(url, config)
  },
}

export { getOrCreateRefreshPromise as refreshAccessToken }

export default request
